import type { ModelInputs } from '../../domain/types'
import { clamp } from './statistics'
import { decimalToImplied } from './odds'
import { decideAction, expectedValue, type EvDecision } from './ev'

function logit(p: number): number {
  const x = clamp(p, 1e-6, 1 - 1e-6)
  return Math.log(x / (1 - x))
}

function expit(x: number): number {
  return 1 / (1 + Math.exp(-x))
}

export interface ModelEstimate {
  estProb: number
  ciLow: number
  ciHigh: number
  uncertainty: number
}

export function estimateProbability(inputs: ModelInputs): ModelEstimate {
  const dq = clamp(inputs.dataQuality, 0, 1)
  const blendBase = inputs.baseProb * (0.5 + 0.5 * dq) + inputs.historicalWinRate * (0.5 - 0.5 * dq)
  let l = logit(clamp(blendBase, 0.01, 0.99))
  l += clamp(inputs.recentForm, -1, 1) * 0.6
  l += clamp(inputs.marketMovement, -1, 1) * 0.4
  const estProb = clamp(expit(l), 0.01, 0.99)

  const uncertainty = clamp(
    (1 - clamp(inputs.confidence, 0, 1)) * 0.5 +
      clamp(inputs.volatility, 0, 1) * 0.3 +
      (1 - dq) * 0.2,
    0,
    1,
  )
  const halfWidth = uncertainty * 0.45
  return {
    estProb,
    ciLow: clamp(estProb - halfWidth, 0, 1),
    ciHigh: clamp(estProb + halfWidth, 0, 1),
    uncertainty,
  }
}

export interface ModelEvaluation extends ModelEstimate {
  marketProb: number
  edge: number
  ev: number
  action: EvDecision
}

export function evaluateModel(
  inputs: ModelInputs,
  decimalOdds: number,
  minEdge = 0.02,
): ModelEvaluation {
  const estimate = estimateProbability(inputs)
  const marketProb = decimalToImplied(decimalOdds)
  const ev = expectedValue({ modelProb: estimate.estProb, decimalOdds, minEdge })
  const edge = estimate.estProb - marketProb
  return {
    ...estimate,
    marketProb,
    edge,
    ev: ev.evPerUnit,
    action: estimate.uncertainty > 0.6 ? 'paper' : decideAction(edge, ev.evPerUnit, minEdge),
  }
}
